import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { OwlMascot } from "@/components/OwlMascot";
import {
  FlaskConical, Atom, Microscope, Sparkles, Play, ShieldCheck,
  Brain, Trophy, GraduationCap, ArrowRight, MessageCircle, Waves,
  Check, Clock
} from "lucide-react";

const Landing = () => {
  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid-lines-light absolute inset-0 pointer-events-none" />
        <div className="glow-blob bg-blue-400 w-[420px] h-[420px] -top-24 -left-24" />
        <div className="glow-blob bg-cyan-300 w-[380px] h-[380px] top-40 -right-24" />
        <div className="glow-blob bg-amber-200 w-[260px] h-[260px] bottom-0 left-1/3" />

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6">
              <Sparkles className="h-3.5 w-3.5" /> AI-powered lab for classes IX–XII
            </div>
            <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-widest" data-testid="hero-trial-ribbon">
              <Clock className="h-3.5 w-3.5" /> 20-day free trial · no card required
            </div>
            <h1 className="font-outfit font-black text-slate-900 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="hero-title">
              Perform <span className="text-blue-600">Physics</span>,{" "}
              <span className="text-fuchsia-600">Chemistry</span> &{" "}
              <span className="text-emerald-600">Biology</span> — with an AI tutor at your side.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Meet <b>Brimi</b>, your friendly owl science tutor. Run safe virtual experiments,
              tweak sliders, watch real reactions unfold, and auto-generate CBSE/ICSE-style lab
              reports. Voice-controlled. Multi-LLM. Delightfully nerdy.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="hero-cta-start">
                <Button size="lg" className="rounded-full bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 transition-transform px-6 h-12 text-base">
                  Start experimenting <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/labs" data-testid="hero-cta-explore">
                <Button size="lg" variant="outline" className="rounded-full border-slate-300 h-12 px-6 text-base hover:bg-white">
                  <Play className="mr-2 h-4 w-4" /> Explore labs
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> 100% safe virtual experiments</div>
              <div className="flex items-center gap-2"><Brain className="h-4 w-4 text-blue-600" /> Claude · GPT · Gemini</div>
              <div className="flex items-center gap-2"><Waves className="h-4 w-4 text-cyan-600" /> Voice tutor</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-blue-500/20 to-cyan-400/10 rounded-[2rem] blur-2xl" />
              <div className="relative rounded-[2rem] bg-white border border-slate-200 shadow-2xl p-8">
                <div className="flex justify-center anim-float">
                  <OwlMascot size={200} talking />
                </div>
                <div className="mt-6 rounded-2xl bg-blue-50 border border-blue-100 p-4">
                  <div className="text-xs uppercase font-bold tracking-widest text-blue-600 mb-1 flex items-center gap-1.5">
                    <MessageCircle className="h-3.5 w-3.5" /> Brimi says
                  </div>
                  <p className="text-slate-800 leading-relaxed">
                    &ldquo;Why does sodium react violently with water? Let me show you — safely — and
                    predict what happens if we double the piece size.&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LAB TRIO */}
      <section className="relative bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="text-blue-600 font-bold uppercase tracking-widest text-xs mb-2">Three labs, one owl</div>
            <h2 className="font-outfit font-black text-4xl sm:text-5xl text-slate-900 leading-tight">
              A safer, more curious way to do science.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Atom, title: "Physics", desc: "Pendulum, Ohm's law, prisms, meter bridge, diode I-V — with real math.", tint: "bg-amber-50 border-amber-200 text-amber-700", path: "/labs/physics" },
              { icon: FlaskConical, title: "Chemistry", desc: "Titrations, pH, electrolysis, safely watch sodium react with water.", tint: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700", path: "/labs/chemistry" },
              { icon: Microscope, title: "Biology", desc: "Virtual microscope, photosynthesis, osmosis, extract DNA from a banana.", tint: "bg-emerald-50 border-emerald-200 text-emerald-700", path: "/labs/biology" },
            ].map((lab) => (
              <Link
                key={lab.title}
                to={lab.path}
                data-testid={`landing-lab-${lab.title.toLowerCase()}`}
                className="group relative rounded-3xl bg-white border border-slate-200 p-8 hover:border-blue-300 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl border ${lab.tint}`}>
                  <lab.icon className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-outfit font-bold text-2xl text-slate-900">{lab.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{lab.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-blue-600 font-semibold">
                  Enter lab <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="relative bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-7 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 p-8 lg:p-10 text-white relative overflow-hidden">
              <div className="glow-blob bg-cyan-300 w-72 h-72 -bottom-16 -right-16" />
              <Brain className="h-8 w-8 mb-4 text-cyan-200" />
              <h3 className="font-outfit font-black text-3xl lg:text-4xl leading-tight">AI mistake detection.</h3>
              <p className="mt-3 text-blue-100 text-lg leading-relaxed max-w-lg">
                Wrong chemical? Bad wiring? Formula slip? Brimi catches it in real time and asks a
                Socratic follow-up — because doing science is about learning from being almost-right.
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm">
                <span className="px-3 py-1 rounded-full bg-white/15 border border-white/25 font-mono">Claude Sonnet 4.5</span>
                <span className="px-3 py-1 rounded-full bg-white/15 border border-white/25 font-mono">GPT 5.2</span>
                <span className="px-3 py-1 rounded-full bg-white/15 border border-white/25 font-mono">Gemini 3 Flash</span>
              </div>
            </div>
            <div className="md:col-span-5 rounded-3xl bg-white border border-slate-200 p-8 lg:p-10">
              <Trophy className="h-8 w-8 mb-4 text-amber-500" />
              <h3 className="font-outfit font-black text-3xl text-slate-900 leading-tight">XP, streaks & badges.</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Every experiment earns XP. Unlock <b>First Experiment</b>, <b>Curious Mind</b>, and
                <b> Lab Ninja</b> as you go.
              </p>
              <div className="mt-6 flex gap-2 flex-wrap">
                {["First Experiment", "Curious Mind", "Lab Ninja"].map((b) => (
                  <span key={b} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700">{b}</span>
                ))}
              </div>
            </div>
            <div className="md:col-span-5 rounded-3xl bg-white border border-slate-200 p-8">
              <GraduationCap className="h-8 w-8 mb-4 text-emerald-600" />
              <h3 className="font-outfit font-bold text-2xl text-slate-900">Teachers get superpowers</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">Assign experiments, monitor completion, and review AI-generated reports at scale.</p>
            </div>
            <div className="md:col-span-7 rounded-3xl bg-slate-900 text-slate-50 p-8 lg:p-10 relative overflow-hidden">
              <div className="grid-lines absolute inset-0 opacity-40" />
              <div className="relative">
                <Waves className="h-8 w-8 mb-4 text-cyan-400" />
                <h3 className="font-outfit font-black text-3xl leading-tight">Talk to Brimi. Really.</h3>
                <p className="mt-3 text-slate-300 leading-relaxed max-w-lg">
                  Ask questions with your voice. Brimi listens (Whisper) and answers out loud (OpenAI TTS).
                  Perfect for hands-full-in-the-lab moments.
                </p>
                <div className="mt-6 flex gap-1 items-end text-cyan-400">
                  <span className="wave-bar" style={{ height: 8 }} />
                  <span className="wave-bar" style={{ height: 16 }} />
                  <span className="wave-bar" style={{ height: 12 }} />
                  <span className="wave-bar" style={{ height: 20 }} />
                  <span className="wave-bar" style={{ height: 10 }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-blue-600 font-bold uppercase tracking-widest text-xs mb-2">Simple pricing</div>
            <h2 className="font-outfit font-black text-4xl sm:text-5xl text-slate-900 leading-tight">
              Start free. Upgrade only if you love it.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Every new account gets a <b className="text-emerald-600">20-day free trial</b> —
              access to all 78+ experiments, AI tutor, voice mode & 3D scenes. No card up front.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {/* Trial card */}
            <div className="rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-8 flex flex-col" data-testid="plan-trial">
              <div className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-widest">
                <Clock className="h-3.5 w-3.5" /> Free trial
              </div>
              <div className="mt-4 font-outfit font-black text-5xl text-slate-900">
                ₹0<span className="text-lg text-slate-500 font-bold">/20 days</span>
              </div>
              <div className="mt-2 text-sm text-slate-600">Full-feature access, no card.</div>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
                {["All 78+ experiments", "AI tutor (Claude · GPT · Gemini)", "Voice mode + 3D scenes", "PDF lab reports", "Class-code collaboration"].map((f) => (
                  <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Link to="/signup" data-testid="plan-trial-cta">
                <Button className="mt-8 w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-11">
                  Start free trial
                </Button>
              </Link>
            </div>

            {/* Quarterly */}
            <div className="rounded-3xl bg-white border border-slate-200 p-8 flex flex-col shadow-lg" data-testid="plan-quarterly">
              <div className="text-slate-500 text-xs font-bold uppercase tracking-widest">Quarterly</div>
              <div className="mt-4 font-outfit font-black text-5xl text-slate-900">
                ₹399<span className="text-lg text-slate-500 font-bold">/3 months</span>
              </div>
              <div className="mt-2 text-sm text-slate-600">₹133/mo · billed every 3 months</div>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
                {["Everything in trial", "Unlimited experiments", "Unlimited AI tutor chat", "Priority support"].map((f) => (
                  <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Link to="/signup" data-testid="plan-quarterly-cta">
                <Button className="mt-8 w-full rounded-full bg-slate-900 hover:bg-slate-800 h-11">
                  Get quarterly
                </Button>
              </Link>
            </div>

            {/* Yearly */}
            <div className="rounded-3xl bg-slate-900 border-2 border-blue-500 p-8 flex flex-col relative shadow-2xl text-white" data-testid="plan-yearly">
              <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-amber-400 text-amber-950 text-[10px] font-black uppercase tracking-widest">
                Save 42%
              </div>
              <div className="text-cyan-300 text-xs font-bold uppercase tracking-widest">Yearly</div>
              <div className="mt-4 font-outfit font-black text-5xl">
                ₹1,399<span className="text-lg text-slate-400 font-bold">/year</span>
              </div>
              <div className="mt-2 text-sm text-slate-300">Just ₹117/mo — best value</div>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-200 flex-1">
                {["Everything in quarterly", "Early access to new experiments", "Save 42% vs quarterly", "Cancel anytime"].map((f) => (
                  <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Link to="/signup" data-testid="plan-yearly-cta">
                <Button className="mt-8 w-full rounded-full bg-blue-600 hover:bg-blue-500 h-11">
                  Get yearly
                </Button>
              </Link>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-slate-500 font-mono">
            You will be charged only after your 20-day free trial ends. Payments are securely processed via Razorpay.
          </p>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="relative border-t border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-outfit font-black text-4xl sm:text-5xl text-slate-900 leading-tight">
            Give your students a lab that never runs out of reagents.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">Free to try. Works on any laptop or tablet.</p>
          <Link to="/signup" data-testid="footer-cta-signup">
            <Button size="lg" className="mt-8 rounded-full bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 transition-transform px-8 h-12 text-base">
              Create free account <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-slate-500 font-mono">
          © BrimiAI Virtual Science Lab · Hoot hoot 🦉
        </div>
      </footer>
    </div>
  );
};

export default Landing;
